import * as React from "react";
import { RenderTemplates } from "./module/RenderTemplates";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, FormDialog, PageHeader, RelatedLinks, StatusPill, type DataListColumn, type FormFieldSpec } from "@ozpanel/kit";
import {
  Button, ConfirmDialog, Panel, Section, Stat, StateView, Tabs, TabsContent, TabsList, TabsTrigger,
  formatRelative, useAuth, useToast, When } from "@ozpanel/ui";

// Platform internals: certificates, the scheduler, the audit trail and intrusion decisions.

interface Cert {
  id: number;
  domain: string;
  issuer: string;
  fingerprint: string;
  days_left: number;
  not_after: string;
  has_key: boolean;
  is_current: boolean;
  recorded_by: string;
}
// How often a task runs, as the scheduler declares and stores it (internal/schedule/interval.go,
// IntervalView — the field names are its JSON tags). The interval is the CLUSTER's setting; a task
// whose cadence is pinned by a safety invariant is not editable and carries the reason instead.
interface TaskInterval {
  key: string;
  default_sec: number;
  effective_sec: number;
  min_sec: number;
  max_sec: number;
  editable: boolean;
  fixed_reason?: string;
  overridden: boolean;
  set_by?: string;
  set_at?: string;
  // A stored value this build refuses to run (outside the bounds), and a node that has not yet
  // begun a wait on the new value: both are "the setting is not what runs", and both are shown.
  ignored?: string;
  note?: string;
  event_watch?: boolean;
  pending?: string;
}

// fmtSec prints seconds the way the API accepts them back: 90 → "1m30s", 120 → "2m", 3600 → "1h".
function fmtSec(n: number): string {
  if (n <= 0) return "—";
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = n % 60;
  return `${h ? `${h}h` : ""}${m ? `${m}m` : ""}${s ? `${s}s` : ""}`;
}

interface Task {
  task: string;
  kind: string;
  interval?: TaskInterval;
  state: string;
  // severity is the SERVER's verdict — "" when the task is fine. The client must not compute
  // its own: internal/schedule/view.go already decides never / failing / late / hung using
  // lateFactor and the run's own result, and a second opinion in the browser can only disagree
  // with the thing that raises the actual alerts.
  severity: string;
  note?: string;
  node: string;
  interval_sec: number;
  ago_sec: number;
  duration_ms: number;
  // The node's bounded work queue (internal/schedule/queue.go): which lane the task runs in and how long
  // its last recorded run waited for a slot. Critical tasks never wait.
  wait_ms?: number;
  lane?: string;
  run_count: number;
  // fail_count is a LIFETIME total; fail_streak is how many in a row RIGHT NOW. Only the second
  // says whether anything is wrong: certs.watch on node10 shows 469 lifetime failures over 1125
  // runs with a streak of 0 — it is working, and painting that total as a warning invents an
  // incident. Showing a number without what governs it, in the screen built to catch exactly that.
  fail_count: number;
  fail_streak: number;
  acked?: boolean;
  last_finished: string;
  // What the run actually SAID. The schema calls this "the whole question an operator is
  // asking": a reconciler reporting "applied through generation 4936" and one reporting
  // nothing are both ok, and only this distinguishes them. Absent via omitempty when the run
  // produced no output, which is itself information — a repair loop that never says what it
  // repaired cannot be reviewed.
  last_output?: string;
  last_error?: string;
  title?: string;
}
interface AuditRow {
  id: number;
  created_at: string;
  principal: string;
  role: string;
  module: string;
  action: string;
  ok: boolean;
  ip: string;
  node: string;
}
interface Decision {
  id?: number;
  value?: string;
  scenario?: string;
  type?: string;
  duration?: string;
  origin?: string;
}

function useJSON<T>(key: string, path: string, refetch?: number) {
  const { client } = useAuth();
  return useQuery({ queryKey: [key], queryFn: () => client.apiFetch<T>(path), refetchInterval: refetch });
}

function CertsTab() {
  const q = useJSON<{ certificates: Cert[] | null; shared: boolean; shared_detail: string; note?: string; unrecorded?: string[] | null; serving?: Record<string, string> }>(
    "certs", "/certs",
  );
  // A certificate matters only for a name something still SERVES. Measured on node10:
  // drilltest.oztag.click and its siblings were 2 days expired and shown in red, while the
  // vhost had been deleted and the name no longer resolved at all — the ACME hold had
  // correctly refused to obtain a public certificate for a name that could not be validated,
  // so Caddy's internal CA issued one and the record outlived the site.
  //
  // Expired-and-orphaned is housekeeping. Expired-and-serving is an outage. Painting both red
  // is how the second one gets lost among the first.
  const vhosts = useJSON<{ items: { domain: string }[] | null }>("caddy-vhosts", "/caddy/vhosts");
  const served = new Set((vhosts.data?.items ?? []).map((v) => v.domain));
  const d = q.data;
  const cols: DataListColumn<Cert>[] = [
    { key: "domain", label: "Domain", sortable: true },
    { key: "issuer", label: "Issuer", sortable: true, render: (c) => (
      // An internal (self-signed) issuer is not a certificate problem to be sorted later — a
      // browser refuses the page. It reads very differently from a short expiry.
      c.issuer && !/let|zerossl|buypass/i.test(c.issuer)
        ? <StatusPill tone="warn">{c.issuer || "internal"}</StatusPill>
        : <span className="text-[12.5px]">{c.issuer}</span>
    ) },
    {
      key: "days_left",
      label: "Expires",
      sortable: true,
      value: (c) => c.days_left,
      render: (c) => {
        const live = served.has(c.domain);
        if (c.days_left >= 0) {
          return <StatusPill tone={c.days_left < 14 ? "warn" : "ok"}>{c.days_left}d</StatusPill>;
        }
        // Expired with nothing serving the name is a stale row, not an incident.
        return live ? (
          <StatusPill tone="bad">EXPIRED — and served</StatusPill>
        ) : (
          <StatusPill tone="neutral">expired, nothing serves it</StatusPill>
        );
      },
    },
    {
      key: "serving",
      label: "Site",
      sortable: true,
      value: (c) => (served.has(c.domain) ? "live" : "none"),
      render: (c) =>
        served.has(c.domain) ? (
          <StatusPill tone="ok">live vhost</StatusPill>
        ) : (
          <span className="text-[11.5px] text-muted">no vhost — orphaned record</span>
        ),
    },
    {
      key: "is_current",
      label: "Serving",
      sortable: true,
      value: (c) => (c.is_current ? "current" : "superseded"),
      render: (c) => (c.is_current ? <StatusPill tone="ok">current</StatusPill> : <span className="text-muted">superseded</span>),
    },
    { key: "has_key", label: "Key", value: (c) => (c.has_key ? "yes" : "no"), render: (c) => (c.has_key ? "yes" : <StatusPill tone="bad">missing</StatusPill>) },
  ];
  return (
    <Section title="Certificates" hint={d ? `${(d.certificates ?? []).length}` : undefined} bodyClassName="p-0">
      <div className="p-4">
        <StateView loading={q.isLoading} error={q.error} errorContext="certificates">
          {d && (
            <>
              <div className="mb-3">
                <StatusPill tone={d.shared ? "ok" : "bad"}>
                  {d.shared ? d.shared_detail : `NOT on the cluster's storage: ${d.shared_detail}`}
                </StatusPill>
              </div>
              <DataList
                items={d.certificates ?? []}
                columns={cols}
                getRowKey={(c) => String(c.id)}
                searchable
                searchPlaceholder="Search domains…"
                emptyMessage="No certificates recorded."
              />
              {/* Certificates Caddy's storage holds that the inventory has no record of yet — the
                  next recording pass picks them up; shown rather than folded into the count. */}
              {(d.unrecorded?.length ?? 0) > 0 && (
                <div className="mt-3 rounded-lg border border-warnsoft bg-warnsoft p-3">
                  <div className="text-[12.5px] text-warn">
                    {d.unrecorded?.length} certificate(s) in Caddy's storage with no record here yet
                  </div>
                  <div className="mt-1 font-mono text-[11.5px] text-muted">{(d.unrecorded ?? []).join(", ")}</div>
                  <div className="mt-1 text-[11.5px] leading-relaxed text-muted">
                    Caddy serves and renews them; they are missing only from this inventory until the
                    next recording pass (every few minutes).
                  </div>
                </div>
              )}
              {d.note && <p className="mt-3 text-[11.5px] leading-relaxed text-muted">{d.note}</p>}
            </>
          )}
        </StateView>
      </div>

    </Section>
  );
}

function ScheduleTab() {
  const q = useJSON<{
    tasks: Task[] | null; open_alerts: number; this_node: string; worst?: string; note?: string;
    queue_now?: { task: string; lane: string; state: string; seconds: number }[] | null;
    queue_limits?: { normal: number; long: number };
    // Whether THIS node could read the interval overrides at all — when it could not, every interval
    // shown is a compiled default (or the last value read), and the page says so.
    intervals?: { read: boolean; read_ago_sec: number; error?: string; note: string };
  }>(
    "schedule", "/schedule", 30000,
  );
  const d = q.data;
  const qcT = useQueryClient();
  const { notify: notifyT } = useToast();
  const { client: cliT } = useAuth();
  const [acking, setAcking] = React.useState<Task | null>(null);
  // Changing how often a task runs. The setting is keyed by the task (not the node, not the
  // instance), so the dialog says it applies to every node — the row it was opened from is only
  // where the operator happened to click.
  const [editing, setEditing] = React.useState<Task | null>(null);
  const [resetting, setResetting] = React.useState<Task | null>(null);
  const [intervalError, setIntervalError] = React.useState<string | null>(null);
  const [savingInterval, setSavingInterval] = React.useState(false);
  const editFields = React.useMemo<FormFieldSpec[]>(() => {
    const iv = editing?.interval;
    if (!iv) return [];
    return [{
      key: "interval",
      label: iv.event_watch ? "Look for its event every" : "Run every",
      required: true,
      placeholder: fmtSec(iv.default_sec),
      defaultValue: fmtSec(iv.effective_sec),
      help:
        `Between ${fmtSec(iv.min_sec)} and ${fmtSec(iv.max_sec)}, in whole seconds (90s, 5m, 1h). ` +
        `The default is ${fmtSec(iv.default_sec)}.` + (iv.note ? ` ${iv.note}` : ""),
    }];
  }, [editing]);
  const saveInterval = (t: Task, interval: string) => {
    setSavingInterval(true);
    setIntervalError(null);
    void cliT
      .apiFetch(`/schedule/${encodeURIComponent(t.task)}/interval`, { method: "PUT", body: JSON.stringify({ interval }) })
      .then(() => {
        setEditing(null);
        notifyT({ kind: "success", title: "Interval stored for the cluster", body: "Each node adopts it within seconds; a node that has not yet is shown as pending." });
        void qcT.invalidateQueries({ queryKey: ["schedule"] });
      })
      // The server's own sentence: the bounds, or the reason this task's interval is fixed.
      .catch((e: unknown) => setIntervalError(e instanceof Error ? e.message : String(e)))
      .finally(() => setSavingInterval(false));
  };

  const cols: DataListColumn<Task>[] = [
    { key: "task", label: "Task", sortable: true, render: (t) => <span className="font-mono text-[12px]">{t.task}</span> },
    // Tasks are per NODE — the same loop appears once per member, and a task failing on the
    // OTHER node is exactly what cannot be seen by logging into this one.
    { key: "node", label: "Node", sortable: true },
    {
      key: "state",
      label: "State",
      sortable: true,
      render: (t) => (
        <StatusPill tone={t.severity === "error" ? "bad" : t.severity === "warn" ? "warn" : t.state === "running" ? "accent" : "ok"}>
          {t.state}
        </StatusPill>
      ),
    },
    {
      key: "fail_streak",
      label: "Failing now",
      sortable: true,
      value: (t) => t.fail_streak ?? 0,
      // The only failure number that means anything at a glance.
      render: (t) =>
        (t.fail_streak ?? 0) > 0 ? (
          <StatusPill tone="bad">{t.fail_streak} in a row</StatusPill>
        ) : (
          <span className="text-muted">no</span>
        ),
    },
    {
      key: "fail_count",
      label: "Lifetime",
      sortable: true,
      value: (t) => t.fail_count,
      // Shown as a RATE and deliberately not styled as an alarm: it is history, and a high rate
      // on a currently-healthy task is worth investigating, not worth waking someone for.
      render: (t) =>
        t.fail_count > 0 ? (
          <span className="text-[12px] text-muted">
            {t.fail_count} / {t.run_count}
            <span className="text-faint"> ({Math.round((t.fail_count / Math.max(t.run_count, 1)) * 100)}%)</span>
          </span>
        ) : (
          <span className="text-muted">clean</span>
        ),
    },
    {
      key: "ago_sec",
      label: "Last run",
      sortable: true,
      value: (t) => t.ago_sec,
      render: (t) => (
        <span className="text-[12px]">
          {t.ago_sec > 0 ? `${Math.round(t.ago_sec)}s ago` : "—"}
          {t.interval_sec > 0 && <span className="text-faint"> / {t.interval_sec}s</span>}
        </span>
      ),
    },
    {
      key: "interval",
      label: "Interval",
      sortable: true,
      value: (t) => t.interval?.effective_sec ?? t.interval_sec ?? 0,
      // What governs the cadence, never the bare number: an operator's value says who set it, a
      // fixed one says WHY it cannot be changed, and a node still running the old value says so.
      render: (t) => {
        const iv = t.interval;
        if (!iv) return <span className="text-[12px] text-faint">{t.interval_sec > 0 ? fmtSec(t.interval_sec) : "—"}</span>;
        if (!iv.editable) {
          return (
            <span className="block text-[11.5px] leading-relaxed text-muted">
              {iv.effective_sec > 0 && <span className="text-[12px] text-fg">{fmtSec(iv.effective_sec)} · </span>}
              fixed{iv.fixed_reason ? ` — ${iv.fixed_reason}` : ""}
            </span>
          );
        }
        return (
          <span className="block text-[12px]">
            {fmtSec(iv.effective_sec)}
            {iv.event_watch && <span className="text-faint"> between looks</span>}
            {iv.overridden ? (
              <span className="text-faint">
                {" "}· set{iv.set_by ? ` by ${iv.set_by}` : ""}{iv.set_at ? ` ${formatRelative(iv.set_at)}` : ""} (default {fmtSec(iv.default_sec)})
              </span>
            ) : (
              <span className="text-faint"> · default</span>
            )}
            {iv.pending && <span className="block text-[11.5px] leading-relaxed text-warn">{iv.pending}</span>}
            {iv.ignored && <span className="block text-[11.5px] leading-relaxed text-warn">{iv.ignored}</span>}
          </span>
        );
      },
    },
    {
      key: "wait_ms",
      label: "Ran for / waited",
      sortable: true,
      value: (t) => t.duration_ms,
      render: (t) => (
        <span className="text-[12px]">
          {t.duration_ms >= 1000 ? `${(t.duration_ms / 1000).toFixed(1)}s` : `${t.duration_ms}ms`}
          <span className={(t.wait_ms ?? 0) > 5000 ? " text-warn" : " text-faint"}>
            {" "}/ {t.lane === "critical" ? "never waits" : (t.wait_ms ?? 0) >= 1000 ? `${((t.wait_ms ?? 0) / 1000).toFixed(1)}s` : `${t.wait_ms ?? 0}ms`}
          </span>
          {t.lane && t.lane !== "normal" && <span className="text-faint"> · {t.lane}</span>}
        </span>
      ),
    },
    {
      key: "last_output",
      label: "What it did",
      value: (t) => t.last_error || t.last_output || "",
      render: (t) =>
        t.last_error ? (
          <span className="break-all text-[11.5px] leading-relaxed text-crit">{t.last_error}</span>
        ) : t.last_output ? (
          <span className="block max-h-16 overflow-auto whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed text-muted">
            {t.last_output.slice(0, 400)}
          </span>
        ) : (
          // Silence is not success. A loop that reconciles things and never reports what it
          // reconciled is one nobody can review — it is only ever "ok".
          <span className="text-[11.5px] text-faint">reports nothing</span>
        ),
    },
    { key: "note", label: "Why", value: (t) => t.note || "", render: (t) => <span className="text-[11.5px] leading-relaxed text-muted">{t.note}</span> },
  ];
  const attention = (d?.tasks ?? []).filter((t) => t.severity);
  const flaky = (d?.tasks ?? [])
    .filter((t) => !t.severity && t.run_count > 50 && t.fail_count / t.run_count > 0.2)
    .sort((a, b) => b.fail_count / b.run_count - a.fail_count / a.run_count);
  return (
    <Section title="Background tasks" hint={d?.this_node} bodyClassName="p-0">
      <div className="p-4">
        <StateView loading={q.isLoading} error={q.error} errorContext="the scheduler">
          {d && (
            <>
              <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Stat label="Registered" value={String((d.tasks ?? []).length)} />
                <Stat label="Open alerts" value={String(d.open_alerts)} tone={d.open_alerts > 0 ? "warn" : undefined} />
                <Stat label="Needing attention" value={String(attention.length)} tone={attention.length > 0 ? "warn" : undefined} />
                <Stat
                  label="Reporting what they did"
                  value={`${(d.tasks ?? []).filter((t) => (t.last_output || "").trim()).length} / ${(d.tasks ?? []).length}`}
                />
              </div>
              {/* What this node is doing at this instant. At most queue_limits.normal ordinary tasks and
                  queue_limits.long long passes run at once; the rest wait their turn here. */}
              <Panel className="mb-3 p-3 text-[12.5px]">
                <b>Right now on {d.this_node}:</b>{" "}
                {(d.queue_now ?? []).filter((e) => e.state === "running").map((e) => `${e.task} (${e.seconds}s)`).join(", ") || "nothing running"}
                {(d.queue_now ?? []).some((e) => e.state === "waiting") && (
                  <span className="text-warn">
                    {" "}· waiting: {(d.queue_now ?? []).filter((e) => e.state === "waiting").map((e) => `${e.task} (${e.seconds}s)`).join(", ")}
                  </span>
                )}
                <span className="text-faint">
                  {" "}· limit {d.queue_limits?.normal ?? "?"} ordinary + {d.queue_limits?.long ?? "?"} long at once; critical tasks never wait
                </span>
              </Panel>
              {/* The intervals below are only as good as this node's reading of the cluster's settings. */}
              {d.intervals && (!d.intervals.read || d.intervals.error) && (
                <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12.5px]">
                  <b>Interval settings were not read on {d.this_node}.</b> {d.intervals.note}
                </Panel>
              )}
              {attention.length > 0 && (
                <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12.5px]">
                  <b>{attention.length} task(s) need attention.</b> A loop that has stopped produces no error,
                  because nothing is executing to produce one — which is why "late" and "never" are failures here
                  rather than absences.
                </Panel>
              )}
              {/* A task that currently succeeds but fails a fifth of the time is not an incident
                  and is not healthy either. It has no severity, so nothing else will ever mention
                  it — and a repair loop that only works most of the time leaves the thing it
                  repairs broken for the rest. */}
              {flaky.length > 0 && (
                <Panel className="mb-3 p-3 text-[12.5px]">
                  <b>Currently succeeding, but historically unreliable:</b>{" "}
                  {flaky.slice(0, 4).map((t) => `${t.task} (${Math.round((t.fail_count / t.run_count) * 100)}%)`).join(", ")}.
                  Not an alert — nothing is failing right now — but a loop that fails this often is not doing its job
                  in between.
                </Panel>
              )}
              <DataList items={d.tasks ?? []} columns={cols} getRowKey={(t) => t.task} searchable searchPlaceholder="Search tasks…"
                rowActions={(t: Task) => (
                  <>
                    {/* Only a task whose interval IS a setting gets the editor; a fixed one shows
                        its reason in the Interval column instead of a control that would 409. */}
                    {t.interval?.editable && (
                      <Button size="sm" variant="ghost" onClick={() => { setIntervalError(null); setEditing(t); }}>Change interval</Button>
                    )}
                    {t.interval?.editable && (t.interval.overridden || t.interval.ignored) && (
                      <Button size="sm" variant="ghost" onClick={() => setResetting(t)}>Reset interval</Button>
                    )}
                    {/* Only a task the SERVER has flagged can be acknowledged. Offering it on a
                        healthy row invites clearing something that was never raised. */}
                    {t.severity ? (
                      <Button size="sm" variant="ghost" onClick={() => setAcking(t)}>Acknowledge</Button>
                    ) : null}
                  </>
                )} />
              {d.note && <p className="mt-3 text-[11.5px] leading-relaxed text-muted">{d.note}</p>}
              {d.intervals?.read && !d.intervals.error && (
                <p className="mt-2 text-[11.5px] leading-relaxed text-muted">{d.intervals.note}</p>
              )}
            </>
          )}
        </StateView>
      </div>

      <FormDialog
        open={!!editing}
        onOpenChange={(o) => { if (!o) { setEditing(null); setIntervalError(null); } }}
        title={`How often ${editing?.interval?.key ?? "this task"} runs`}
        description="This is the cluster's setting: it is stored once and every node adopts it within seconds, with no restart. It applies to every node (and every instance) of this task, not only the row it was opened from."
        submitLabel="Save"
        pending={savingInterval}
        error={intervalError}
        fields={editFields}
        onSubmit={(v) => { if (editing) saveInterval(editing, String(v.interval ?? "").trim()); }}
      />
      <ConfirmDialog
        open={!!resetting}
        onOpenChange={(o) => !o && setResetting(null)}
        title={`Reset the interval of ${resetting?.interval?.key ?? "this task"}?`}
        confirmLabel="Reset"
        body={
          <p>
            The stored value is removed and every node returns to the compiled default
            ({fmtSec(resetting?.interval?.default_sec ?? 0)}) within seconds.
          </p>
        }
        onConfirm={() => {
          const t = resetting;
          setResetting(null);
          if (!t) return;
          void cliT
            .apiFetch(`/schedule/${encodeURIComponent(t.task)}/interval`, { method: "DELETE" })
            .then(() => {
              notifyT({ kind: "success", title: "Interval reset to its default" });
              void qcT.invalidateQueries({ queryKey: ["schedule"] });
            })
            .catch((e: unknown) => notifyT({ kind: "error", title: "Could not reset it", body: e instanceof Error ? e.message : String(e) }));
        }}
      />
      <ConfirmDialog
        open={!!acking}
        onOpenChange={(o) => !o && setAcking(null)}
        title={`Acknowledge ${acking?.task ?? "this task"}?`}
        confirmLabel="Acknowledge"
        body={
          <p>
            It stops being reported until it fails again. Acknowledging does not run it or fix it —
            if the task is failing it will keep failing, quietly, until the next failure raises it
            afresh.
          </p>
        }
        onConfirm={() => {
          const t = acking;
          setAcking(null);
          if (!t) return;
          void cliT
            .apiFetch(`/schedule/${encodeURIComponent(t.task)}/ack`, { method: "POST" })
            .then(() => {
              notifyT({ kind: "success", title: "Acknowledged" });
              void qcT.invalidateQueries({ queryKey: ["schedule"] });
            })
            .catch((e: unknown) => notifyT({ kind: "error", title: "Could not acknowledge it", body: e instanceof Error ? e.message : String(e) }));
        }}
      />
    </Section>
  );
}

function AuditTab() {
  const q = useJSON<{ items: AuditRow[] | null; total: number }>("audit", "/ozpanel/audit");
  const cols: DataListColumn<AuditRow>[] = [
    { key: "created_at", label: "When", sortable: true, value: (a) => a.created_at, render: (a) => <When value={a.created_at} /> },
    { key: "principal", label: "Who", sortable: true },
    { key: "role", label: "Role", sortable: true },
    { key: "action", label: "Action", sortable: true, render: (a) => <span className="font-mono text-[11.5px]">{a.action}</span> },
    { key: "module", label: "Module", sortable: true },
    {
      key: "ok",
      label: "Result",
      sortable: true,
      value: (a) => (a.ok ? "ok" : "refused"),
      // A refused attempt is worth as much as a successful one — more, usually.
      render: (a) => <StatusPill tone={a.ok ? "ok" : "warn"}>{a.ok ? "ok" : "refused"}</StatusPill>,
    },
    { key: "ip", label: "From", sortable: true, render: (a) => <span className="font-mono text-[11.5px]">{a.ip}</span> },
    { key: "node", label: "Node", sortable: true },
  ];
  return (
    <Section title="Audit trail" hint={q.data ? `${q.data.total} most recent` : undefined} bodyClassName="p-0">
      <div className="p-4">
        <DataList
          items={q.data?.items ?? []}
          columns={cols}
          getRowKey={(a) => String(a.id)}
          searchable
          searchPlaceholder="Search actions, principals…"
          loading={q.isLoading}
          error={q.error}
          errorContext="the audit trail"
          emptyMessage="Nothing audited yet."
        />
      </div>
    </Section>
  );
}

function IntrusionTab() {
  const dec = useJSON<{ decisions: Decision[] | null; total: number }>("cs-decisions", "/crowdsec/decisions", 30000);
  const bou = useJSON<{ bouncers: Record<string, unknown>[] | null; total: number }>("cs-bouncers", "/crowdsec/bouncers");
  const cols: DataListColumn<Decision>[] = [
    { key: "value", label: "Address", sortable: true, value: (d) => d.value || "", render: (d) => <span className="font-mono text-[12px]">{d.value}</span> },
    { key: "scenario", label: "Why", sortable: true, value: (d) => d.scenario || "" },
    { key: "type", label: "Action", sortable: true, value: (d) => d.type || "" },
    { key: "duration", label: "For", value: (d) => d.duration || "" },
    { key: "origin", label: "Source", sortable: true, value: (d) => d.origin || "" },
  ];
  const bouncers = (bou.data?.bouncers ?? []) as { name?: string; last_pull?: string; revoked?: boolean; type?: string }[];
  // Manual decisions (POST /crowdsec/decisions/add|delete): an operator banning an address mid-incident,
  // or lifting a ban on a customer's own office. Both were a cscli on a node.
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [banOpen, setBanOpen] = React.useState(false);
  const [banError, setBanError] = React.useState<string | null>(null);
  const [banning, setBanning] = React.useState(false);
  const [lifting, setLifting] = React.useState<Decision | null>(null);
  const [liftBusy, setLiftBusy] = React.useState(false);
  const ban = async (v: Record<string, unknown>) => {
    setBanning(true);
    setBanError(null);
    try {
      const ip = String(v.ip ?? "").trim();
      await client.apiFetch("/crowdsec/decisions/add", {
        method: "POST",
        body: JSON.stringify({ ip, duration: String(v.duration ?? "").trim(), reason: String(v.reason ?? "").trim() }),
      });
      notify({ kind: "success", title: `${ip} banned` });
      setBanOpen(false);
      void qc.invalidateQueries({ queryKey: ["cs-decisions"] });
    } catch (e) {
      setBanError(e instanceof Error ? e.message : String(e));
    } finally {
      setBanning(false);
    }
  };
  const lift = async (d: Decision) => {
    setLiftBusy(true);
    try {
      // By id when the row carries one (exactly this decision); by address otherwise (every decision on it).
      await client.apiFetch("/crowdsec/decisions/delete", {
        method: "POST",
        body: JSON.stringify(d.id != null ? { id: String(d.id) } : { ip: d.value ?? "" }),
      });
      notify({ kind: "success", title: `decision on ${d.value} removed` });
      void qc.invalidateQueries({ queryKey: ["cs-decisions"] });
    } catch (e) {
      notify({ kind: "error", title: "the decision was not removed", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setLiftBusy(false);
      setLifting(null);
    }
  };
  return (
    <>
      <Section title="Enforcers" hint={bou.data ? `${bou.data.total}` : undefined}>
        <StateView loading={bou.isLoading} error={bou.error} errorContext="bouncers">
          {/* A decision nothing enforces is a decision that does nothing. The bouncer's LAST PULL
              is what says whether the firewall is still being told about them. */}
          <div className="space-y-1.5">
            {bouncers.map((b, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 text-[12.5px]">
                <StatusPill tone={b.revoked ? "bad" : "ok"}>{b.revoked ? "revoked" : "active"}</StatusPill>
                <span className="font-mono">{b.name}</span>
                <span className="text-muted">{b.type}</span>
                <span className="text-[11.5px] text-muted">
                  last pulled {b.last_pull ? formatRelative(b.last_pull) : "never"}
                </span>
              </div>
            ))}
            {bouncers.length === 0 && (
              <p className="text-[12.5px] text-crit">
                No enforcer is registered, so decisions below are recorded and never applied.
              </p>
            )}
          </div>
        </StateView>
      </Section>
      <Section
        title="Active decisions"
        className="mt-6"
        hint={dec.data ? `${dec.data.total}` : undefined}
        bodyClassName="p-0"
        actions={<Button size="sm" variant="accent" onClick={() => { setBanError(null); setBanOpen(true); }}>Ban an address</Button>}
      >
        <div className="p-4">
          <DataList
            items={dec.data?.decisions ?? []}
            columns={cols}
            rowActions={(d) => (
              <Button size="sm" variant="ghost" disabled={liftBusy} onClick={() => setLifting(d)}>Lift</Button>
            )}
            getRowKey={(d) => String(d.id ?? d.value)}
            loading={dec.isLoading}
            error={dec.error}
            errorContext="intrusion decisions"
            emptyMessage="Nothing is currently blocked."
          />
        </div>
      </Section>
      <FormDialog
        open={banOpen}
        onOpenChange={(o) => { if (!o) { setBanOpen(false); setBanError(null); } }}
        title="Ban an address"
        description="Adds a ban decision to CrowdSec (cscli decisions add). Every enforcer pulling from it drops the address's traffic at the firewall, within its next pull. A ban on an address your own nodes or admins use locks them out: check it first."
        submitLabel="Ban"
        pending={banning}
        error={banError}
        fields={[
          { key: "ip", label: "IP address", required: true, placeholder: "203.0.113.7" },
          { key: "duration", label: "For", placeholder: "4h", help: "e.g. 4h, 24h, 7d — empty uses CrowdSec's default (4h)" },
          { key: "reason", label: "Reason", help: "recorded with the decision" },
        ]}
        onSubmit={(v) => void ban(v)}
      />
      <ConfirmDialog
        open={!!lifting}
        onOpenChange={(o) => !o && setLifting(null)}
        title={`Lift the decision on ${lifting?.value ?? "this address"}?`}
        confirmLabel="Lift"
        pending={liftBusy}
        body={`Its traffic is accepted again once the enforcers next pull. If ${lifting?.scenario ? `"${lifting.scenario}"` : "the scenario"} fires again it is banned again.`}
        onConfirm={() => { if (lifting) void lift(lifting); }}
      />
    </>
  );
}

// Background tasks get their OWN screen and nav entry, next to Jobs.
//
// They were a tab inside a screen labelled "Certificates & audit", which is where they went
// unfound: the label did not mention them, and the two job systems (recurring loops here,
// stepped async jobs at /jobs) sat in unrelated places with nothing pointing between them.
export function TasksOps() {
  return (
    <div>
      <ScheduleTab />
      {/* The other job system. Stepped one-off work (bootstrap, cluster join) lives in
          internal/job with its own per-step event stream, retry and cancel — a different model
          for a genuinely different thing, but an operator asking "what has this node been
          doing" should not have to already know that. */}
      <RelatedLinks
        links={[
          { label: "Jobs", to: "/jobs", hint: "one-off stepped work: bootstrap, cluster join — with per-step events, retry and cancel" },
          { label: "Certificates & audit", to: "/platform", hint: "what changed, and who changed it" },
        ]}
      />
    </div>
  );
}

export function PlatformScreen() {
  return (
    <div>
      <PageHeader title="Platform" subtitle="Certificates, background tasks, the audit trail and intrusion decisions" />
      <Tabs defaultValue="certs">
        <TabsList>
          <TabsTrigger value="certs">Certificates</TabsTrigger>
          <TabsTrigger value="audit">Audit</TabsTrigger>
          <TabsTrigger value="intrusion">Intrusion</TabsTrigger>
          {/* The repair path CLAUDE.md's rule 3 exists to provide: templates live on disk so a node
              can be fixed without a rebuild. Until now that was reachable only over SSH — which is
              precisely what failed during the node11 drill while HTTPS kept answering. */}
          <TabsTrigger value="templates">Config templates</TabsTrigger>
        </TabsList>
        <TabsContent value="certs"><CertsTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
        <TabsContent value="intrusion"><IntrusionTab /></TabsContent>
        <TabsContent value="templates"><RenderTemplates /></TabsContent>
      </Tabs>
    </div>
  );
}
